import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { FIELD_TYPES, mappingOptionsFor, slugify } from './mappingOptions';

const HAS_OPTIONS = ['select', 'radio'];

function emptyField(index) {
  return {
    uid: `f_${index}_${Math.round(Math.random() * 1e6)}`,
    key: '',
    label: '',
    type: 'text',
    required: false,
    mapping: 'none',
    placeholder: '',
    help: '',
    width: 'full',
    options: [],
  };
}

const SortableFieldRow = ({ field, leadType, onChange, onDelete, usedKeys }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [keyEdited, setKeyEdited] = useState(Boolean(field.key));

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: field.uid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const mappingOptions = mappingOptionsFor(leadType);

  function patch(changes) {
    onChange({ ...field, ...changes });
  }

  function handleLabelChange(value) {
    const changes = { label: value };
    // Auto-derive the key from the label until the user edits the key manually.
    if (!keyEdited) {
      let candidate = slugify(value);
      // ensure uniqueness against the other fields
      let unique = candidate;
      let i = 2;
      while (usedKeys.includes(unique)) unique = `${candidate}_${i++}`;
      changes.key = unique;
    }
    patch(changes);
  }

  const duplicateKey = field.key && usedKeys.includes(field.key);

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        border: `1px solid ${colors.glass}`,
        borderRadius: 1,
        bgcolor: colors.card,
        mb: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
        <Box {...attributes} {...listeners} sx={{ cursor: 'grab', display: 'flex', color: colors.text, opacity: 0.4 }}>
          <DragIndicatorIcon />
        </Box>
        <TextField
          size="small"
          label={t('Label')}
          value={field.label}
          onChange={(e) => handleLabelChange(e.target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          select
          label={t('Type')}
          value={field.type}
          onChange={(e) => patch({ type: e.target.value })}
          sx={{ width: 130 }}
        >
          {FIELD_TYPES.map((ft) => (
            <MenuItem key={ft.value} value={ft.value}>{t(ft.label)}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          label={t('Mapping')}
          value={field.mapping || 'none'}
          onChange={(e) => patch({ mapping: e.target.value })}
          sx={{ width: 220 }}
        >
          {mappingOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        <Tooltip title={t('Required')}>
          <Switch
            size="small"
            checked={Boolean(field.required)}
            onChange={(e) => patch({ required: e.target.checked })}
          />
        </Tooltip>
        <IconButton size="small" onClick={() => setOpen((v) => !v)}>
          <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
        </IconButton>
        <IconButton size="small" onClick={onDelete} sx={{ color: colors.error }}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, p: 2, pt: 0 }}>
          <TextField
            size="small"
            label={t('Field key')}
            value={field.key}
            error={duplicateKey}
            helperText={duplicateKey ? t('Key must be unique') : '%%' + (field.key || 'key') + '%%'}
            onChange={(e) => { setKeyEdited(true); patch({ key: slugify(e.target.value) }); }}
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            label={t('Placeholder')}
            value={field.placeholder || ''}
            onChange={(e) => patch({ placeholder: e.target.value })}
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            select
            label={t('Width')}
            value={field.width || 'full'}
            onChange={(e) => patch({ width: e.target.value })}
            sx={{ width: 130 }}
          >
            <MenuItem value="full">{t('Full')}</MenuItem>
            <MenuItem value="half">{t('Half')}</MenuItem>
          </TextField>
          <TextField
            size="small"
            label={t('Help text')}
            value={field.help || ''}
            onChange={(e) => patch({ help: e.target.value })}
            sx={{ flex: 1, minWidth: 200 }}
          />
          {HAS_OPTIONS.includes(field.type) && (
            <TextField
              size="small"
              label={t('Options (comma separated)')}
              value={(field.options || []).join(', ')}
              onChange={(e) =>
                patch({ options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean) })
              }
              sx={{ width: '100%' }}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const WebFormFieldBuilder = ({ fields = [], leadType = 'person', onChange }) => {
  const { t } = useTranslation();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((f) => f.uid === active.id);
    const newIndex = fields.findIndex((f) => f.uid === over.id);
    onChange(arrayMove(fields, oldIndex, newIndex));
  }

  function updateField(index, updated) {
    const next = [...fields];
    next[index] = updated;
    onChange(next);
  }

  function deleteField(index) {
    onChange(fields.filter((_, i) => i !== index));
  }

  function addField() {
    onChange([...fields, emptyField(fields.length)]);
  }

  return (
    <Box>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={fields.map((f) => f.uid)} strategy={verticalListSortingStrategy}>
          {fields.map((field, index) => (
            <SortableFieldRow
              key={field.uid}
              field={field}
              leadType={leadType}
              usedKeys={fields.filter((_, i) => i !== index).map((f) => f.key)}
              onChange={(updated) => updateField(index, updated)}
              onDelete={() => deleteField(index)}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button startIcon={<AddIcon />} onClick={addField} size="small" sx={{ mt: 1, textTransform: 'none' }}>
        {t('Add field')}
      </Button>
    </Box>
  );
};

export default WebFormFieldBuilder;
